import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, resolveImage } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { PageLoader, EmptyState } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Flag, Trash2 } from "lucide-react";

export function AdminReports() {
  const qc = useQueryClient();
  const [removeFor, setRemoveFor] = useState(null);
  const [reason, setReason] = useState("");
  const { data: reports, isLoading } = useQuery({ queryKey: ["admin-reports"], queryFn: () => apiFetch("/admin/reports") });

  const remove = async () => {
    try {
      await apiFetch(`/listings/${removeFor.listing_id}?reason=${encodeURIComponent(reason.trim())}`, { method: "DELETE" });
      toast.success("Listing removed");
      setRemoveFor(null); setReason("");
      qc.invalidateQueries({ queryKey: ["admin-reports"] });
    } catch (err) { toast.error(err.message); }
  };

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-3">
      {reports.length === 0 ? (
        <EmptyState icon={Flag} title="No open reports" />
      ) : (
        reports.map((r) => (
          <div key={r.id} className="flex gap-3 rounded-2xl border border-border p-3" data-testid={`report-${r.id}`}>
            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-secondary">
              {r.listing?.images?.[0] && <img src={resolveImage(r.listing.images[0])} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="min-w-0 flex-1">
              {r.listing ? <Link to={`/listing/${r.listing.id}`} className="truncate font-medium hover:underline">{r.listing.title}</Link> : <p className="text-muted-foreground">Listing removed</p>}
              <p className="text-sm text-red-600">"{r.reason}"</p>
              <p className="text-xs text-muted-foreground">by {r.reporter_name} · {timeAgo(r.created_at)}</p>
            </div>
            <button onClick={() => setRemoveFor(r)} className="flex h-9 items-center gap-1 self-start rounded-lg bg-destructive px-3 text-xs font-semibold text-white" data-testid={`remove-listing-${r.id}`}><Trash2 className="h-3.5 w-3.5" />Remove</button>
          </div>
        ))
      )}

      <Dialog open={!!removeFor} onOpenChange={(o) => !o && setRemoveFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Remove listing</DialogTitle></DialogHeader>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason (shown to seller)" className="w-full rounded-xl border border-input p-3 text-sm outline-none" data-testid="remove-reason" />
          <DialogFooter><button onClick={remove} disabled={!reason.trim()} className="rounded-xl bg-destructive px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="confirm-remove">Remove listing</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
